import { API_BASE } from '@/shared/services/api';
import { useState, useEffect, useCallback } from 'react';

const API_URL = API_BASE;

export const useLandingConfig = () => {
  const [config, setConfig] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('ezerpToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/landing/config`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.exists) {
        setConfig(data.config);
        setSections(data.sections || []);
      } else {
        setConfig(null);
        setSections([]);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const initializeFromTemplate = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/landing/config/initialize`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        await fetchConfig();
        return { success: true, data };
      }
      return { success: false, error: data.detail };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, fetchConfig]);

  const updateConfig = useCallback(async (updates) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/config`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (response.ok) {
        setConfig(prev => ({ ...prev, ...updates }));
        return { success: true, data };
      }
      return { success: false, error: data.detail };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [getAuthHeaders]);

  const createSection = useCallback(async (sectionData) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/sections`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(sectionData),
      });
      const data = await response.json();
      if (response.ok) {
        await fetchConfig();
        return { success: true, data };
      }
      return { success: false, error: data.detail };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [getAuthHeaders, fetchConfig]);

  const updateSection = useCallback(async (sectionId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/sections/${sectionId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (response.ok) {
        setSections(prev => prev.map(s => s.id === sectionId ? { ...s, ...updates } : s));
        return { success: true, data };
      }
      return { success: false, error: data.detail };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [getAuthHeaders]);

  const deleteSection = useCallback(async (sectionId) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/sections/${sectionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        setSections(prev => prev.filter(s => s.id !== sectionId));
        return { success: true };
      }
      const data = await response.json();
      return { success: false, error: data.detail };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [getAuthHeaders]);

  const reorderSections = useCallback(async (items) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/sections/reorder`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(items),
      });
      if (response.ok) {
        await fetchConfig();
        return { success: true };
      }
      const data = await response.json();
      return { success: false, error: data.detail };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [getAuthHeaders, fetchConfig]);

  const createContentBlock = useCallback(async (sectionId, blockData) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/sections/${sectionId}/blocks`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(blockData),
      });
      const data = await response.json();
      if (response.ok) {
        await fetchConfig();
        return { success: true, data };
      }
      return { success: false, error: data.detail };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [getAuthHeaders, fetchConfig]);

  const updateContentBlock = useCallback(async (blockId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/blocks/${blockId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (response.ok) {
        await fetchConfig();
        return { success: true, data };
      }
      return { success: false, error: data.detail };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [getAuthHeaders, fetchConfig]);

  const deleteContentBlock = useCallback(async (blockId) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/blocks/${blockId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        await fetchConfig();
        return { success: true };
      }
      const data = await response.json();
      return { success: false, error: data.detail };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [getAuthHeaders, fetchConfig]);

  const updateTranslation = useCallback(async (blockId, translationData) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/blocks/${blockId}/translation`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(translationData),
      });
      const data = await response.json();
      if (response.ok) {
        await fetchConfig();
        return { success: true, data };
      }
      return { success: false, error: data.detail };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [getAuthHeaders, fetchConfig]);

  const publish = useCallback(async (versionLabel, notes) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/publish`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ version_label: versionLabel, notes }),
      });
      const data = await response.json();
      if (response.ok) {
        await fetchConfig();
        return { success: true, data };
      }
      return { success: false, error: data.detail };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [getAuthHeaders, fetchConfig]);

  const getVersions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/landing/versions`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      return { success: true, versions: data.versions || [] };
    } catch (err) {
      return { success: false, error: err.message, versions: [] };
    }
  }, [getAuthHeaders]);

  const rollback = useCallback(async (versionId) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/rollback`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ version_id: versionId }),
      });
      const data = await response.json();
      if (response.ok) {
        await fetchConfig();
        return { success: true, data };
      }
      return { success: false, error: data.detail };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [getAuthHeaders, fetchConfig]);

  const getAuditLogs = useCallback(async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await fetch(`${API_URL}/api/landing/audit?${queryParams}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      return { success: true, logs: data.audit_logs || [] };
    } catch (err) {
      return { success: false, error: err.message, logs: [] };
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    config,
    sections,
    loading,
    error,
    fetchConfig,
    initializeFromTemplate,
    updateConfig,
    createSection,
    updateSection,
    deleteSection,
    reorderSections,
    createContentBlock,
    updateContentBlock,
    deleteContentBlock,
    updateTranslation,
    publish,
    getVersions,
    rollback,
    getAuditLogs,
  };
};

export default useLandingConfig;
